import { useEffect, useRef, useState } from "react";
import { Form, Image, Alert, Steps, Flex, Typography, message } from "antd";
import CustomButton from "../../components/CustomButton/Button";
import Logo from "../../assets/images/auth-logo.png";
import SuccessGif from "../../assets/images/success.gif";
import CustomModal from "../../components/CustomModal/CustomModal";
import OtpVerification from "../Signup/OtpVerification";
import {
  signupUser,
  getDropdownData,
  clearAuthError,
  getRmCities,
  sendOtp,
  getBusByPan,
} from "../../redux/slices/AuthSlice";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import AuthDesign from "../../components/AuthDesign/AuthDesign";
import BasicInfoForm from "./BasicInfoForm";
import DealDetailsForm from "./DealDetailsForm";
import DealInformation from "./DealInformation";
import "./Signup.scss";
import BackButton from "../../components/CustomButton/BackButton";
import { SUCCESS_MSGS } from "../../constants/constants";
import { formatReferenceLinks } from "../../helpers/referenceLinks";
import {
  isEquityCategory,
  isInvoiceStyleCategory,
  isVenturesCategory,
} from "../../helpers/dealCategoryProducts";

const { Text } = Typography;

const step1Fields = ["contactName", "website"];

const Signup = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const location = useLocation();
  const { signupType } = location.state;
  const phoneNumber = Form.useWatch("pocPhoneNumber", form);
  const email = Form.useWatch("contactEmail", form);
  const { dropdownData, error, loading, allCities } = useSelector(
    (state) => state.auth,
  );
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [isVerified, setIsVerified] = useState({
    email: false,
    mobile: false,
  });

  const [verifiedEmail, setVerifiedEmail] = useState("");
  const [verifiedMobile, setVerifiedMobile] = useState("");
  const [otpContext, setOtpContext] = useState({ type: "", value: "" });
  const [currentStep, setCurrentStep] = useState(0);
  const [fileUrl, setFileUrl] = useState(null);
  const [typeDisabled, setTypeDisabled] = useState(false);
  const [annualTurnoverLocked, setAnnualTurnoverLocked] = useState(false);
  const [showSuccessGif, setShowSuccessGif] = useState(false);

  const formSectionRef = useRef(null);
  // Avoid re-fetching / wiping Basic Info when user navigates Back to step 1
  const fetchedPanRef = useRef(null);

  const dealCategoryId = signupType?.dealCategoryId || null;
  const isID = isInvoiceStyleCategory(dealCategoryId);
  const isJv = isVenturesCategory(dealCategoryId);
  const isSF = isEquityCategory(dealCategoryId);

  useEffect(() => {
    dispatch(getDropdownData());
    dispatch(getRmCities());
    return () => {
      dispatch(clearAuthError());
    };
  }, [dispatch]);

  // Reset verification if user changes value
  useEffect(() => {
    if (verifiedEmail && email && email !== verifiedEmail) {
      setIsVerified((prev) => ({
        ...prev,
        email: false,
      }));
    }
  }, [email, verifiedEmail]);

  useEffect(() => {
    if (verifiedMobile && phoneNumber && phoneNumber !== verifiedMobile) {
      setIsVerified((prev) => ({
        ...prev,
        mobile: false,
      }));
    }
  }, [phoneNumber, verifiedMobile]);

  // Autofill Basic Info from existing business (once per PAN). Do not clear user input on Back.
  useEffect(() => {
    const fetchBusDetails = async () => {
      if (currentStep !== 1) return;

      const pan = form.getFieldValue("busPan");
      if (!pan || fetchedPanRef.current === pan) return;

      try {
        const res = await dispatch(getBusByPan(pan));
        const data = res?.payload?.response;
        fetchedPanRef.current = pan;

        if (data) {
          form.setFieldsValue({
            industryType: data?.industryTypeId || undefined,
            busType: data?.businessTypeId || undefined,
            annualTurnover: data?.annualTurnoverId || undefined,
            contactEmail: data?.email || undefined,
            busName: data?.busName || undefined,
            contactName: data?.contactName || undefined,
            pocPhoneNumber: data?.contactPhoneNumber || undefined,
            website: data?.busWebsite || undefined,
          });
          if (data?.kycDetails?.kycEmail?.isVerified) {
            setIsVerified((prev) => ({
              ...prev,
              email: true,
            }));
            setVerifiedEmail(data?.email);
          }
          if (data?.kycDetails?.kycMobile?.isVerified) {
            setIsVerified((prev) => ({
              ...prev,
              mobile: true,
            }));
            setVerifiedMobile(data?.contactPhoneNumber);
          }
          setTypeDisabled(true);
          setAnnualTurnoverLocked(Boolean(data?.annualTurnoverId));
        } else {
          // New business — unlock fields but keep anything the user already typed
          setTypeDisabled(false);
          setAnnualTurnoverLocked(false);
        }
      } catch (err) {
        console.error("Failed to fetch business details", err);
        fetchedPanRef.current = pan;
        setTypeDisabled(false);
        setAnnualTurnoverLocked(false);
      }
    };

    fetchBusDetails();
  }, [currentStep, dispatch, form]);

  const scrollFormToTop = () => {
    setTimeout(() => {
      if (formSectionRef.current) {
        formSectionRef.current.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }
    }, 50);
  };

  const handleNext = async () => {
    try {
      // Step wise validation
      if (currentStep === 0) {
        const stepZeroFields = ["busPan"];
        if (isInvoiceStyleCategory(dealCategoryId)) {
          stepZeroFields.push("gstin");
        }
        await form.validateFields(stepZeroFields);
      }

      if (currentStep === 1) {
        await form.validateFields(step1Fields);

        if (!isVerified.email || !isVerified.mobile) {
          return;
        }
      }

      // Move to next step
      setCurrentStep((prev) => prev + 1);
      scrollFormToTop();
    } catch {
      // validation failed
    }
  };

  const handlePrev = () => {
    setCurrentStep((prev) => prev - 1);
    scrollFormToTop();
  };

  const handleVerifyBtnClick = async (type) => {
    if (type === "mobile") {
      await dispatch(sendOtp({ mobile: phoneNumber }));
      setOtpContext({
        type: "mobile",
        value: phoneNumber,
      });
      setOtpModalOpen(true);
    } else {
      await dispatch(sendOtp({ email }));
      setOtpContext({ type: "email", value: email });
      setOtpModalOpen(true);
    }
  };

  const handleSubmit = async (values) => {
    let payload = {
      isExistingBusiness: typeDisabled,
      busName: values.busName,
      businessTypeId: values.busType,
      industryTypeId: values.industryType,
      email: values.contactEmail,
      website: values.website?.trim()
        ? `https://${values.website.trim().replace(/^(https?:\/\/)+/gi, "")}`
        : undefined,
      pocName: values.contactName,
      phoneNumber: values.pocPhoneNumber,
      // city: values.location,
      dealCategoryId: signupType?.dealCategoryId,
      pan: values.busPan,
    };

    if (isID) {
      const idPayload = {
        gstin: values.gstin,
        expectedCredit: values.expAmt,
        expectedTenure: values.tenure,
        annualTurnoverId: values.annualTurnover,
        entName: values.entName,
      };
      payload = { ...payload, ...idPayload };
    } else if (isJv) {
      // Read optional fields from form directly so Form.List values are not dropped
      const otherLinks = values.otherLinks ?? form.getFieldValue("otherLinks");
      const jvPayload = {
        pitchDeck: fileUrl,
        amountToRaise: values.amtToRaise,
        dealDetails: values.dealDetails,
        otherTermsConditions: values.terms,
        referenceLinks: formatReferenceLinks(otherLinks),
      };
      payload = { ...payload, ...jvPayload };
    } else if (isSF) {
      // Read optional fields from form directly so Form.List values are not dropped
      const otherLinks = values.otherLinks ?? form.getFieldValue("otherLinks");
      const jvPayload = {
        pitchDeck: fileUrl,
        amountToRaise: values.amtToRaise,
        dealDetails: values.dealDetails,
        otherTermsConditions: values.terms,
        referenceLinks: formatReferenceLinks(otherLinks),
      };
      payload = { ...payload, ...jvPayload };
    }

    const res = await dispatch(signupUser(payload));

    if (res?.payload?.statusCode === 201) {
      form.resetFields();

      // Show success message based on deal type
      let successMsg = "Business signup completed successfully.";
      if (isJv || isSF) {
        successMsg = SUCCESS_MSGS.JV_SIGNUP_SUCCESS;
      } else if (isID) {
        successMsg = SUCCESS_MSGS.ID_SIGNUP_SUCCESS;
      }

      message.success(successMsg);
      setShowSuccessGif(true);

      // Navigate to login after showing the message
      setTimeout(() => {
        navigate("/login");
      }, 1500);
    }
  };

  const signupForm = (
    <div className="auth-signup-container">
      <div className="auth-header">
        <div className="logo">
          <Image preview={false} src={Logo} alt="Logo" />
        </div>
        <Alert
          message="If you have already registered for other deal type. Please use the same email here"
          type="info"
          closable
          style={{ marginBottom: 10 }}
        />
        <BackButton />
        <Steps
          current={currentStep}
          onChange={() => {}}
          items={[
            { title: "Deal Information" },
            { title: "Basic Info" },
            { title: "Deal Details" },
          ]}
          style={{ marginBottom: 20 }}
        />
        <Text>
          Selected Deal Type: <Text strong>{signupType?.label || signupType?.dealCategoryName}</Text>
        </Text>
      </div>
      <Form
        form={form}
        layout="vertical"
        className="signup-form"
        onFinish={handleSubmit}
        initialValues={{ remember: true, countryCode: "+91" }}
      >
        <div className="auth-form-section" ref={formSectionRef}>
          <div style={{ display: currentStep === 0 ? "block" : "none" }}>
            <DealInformation dealTypeId={dealCategoryId} />
          </div>
          <div
            style={{
              display: currentStep === 1 ? "block" : "none",
            }}
          >
            <BasicInfoForm
              isVerified={isVerified}
              handleVerifyBtnClick={handleVerifyBtnClick}
              email={email}
              phoneNumber={phoneNumber}
              dealTypeId={dealCategoryId}
              typeDisabled={typeDisabled}
            />
          </div>

          <div style={{ display: currentStep === 2 ? "block" : "none" }}>
            <DealDetailsForm
              dropdownData={dropdownData}
              allCities={allCities}
              dealTypeId={dealCategoryId}
              setFileUrl={setFileUrl}
              typeDisabled={typeDisabled}
              annualTurnoverLocked={annualTurnoverLocked}
            />
          </div>
        </div>

        <div className="auth-footer">
          {currentStep === 0 && (
            <CustomButton
              type="primary"
              htmlType="button"
              onClick={handleNext}
              style={{ padding: 24 }}
              block
            >
              Next
            </CustomButton>
          )}

          {(currentStep === 1 || currentStep === 2) && (
            <Flex align="center" justify="space-between">
              <CustomButton onClick={handlePrev} style={{ padding: 24 }}>
                Back
              </CustomButton>

              {currentStep === 1 && (
                <CustomButton
                  loading={loading}
                  type="primary"
                  htmlType="button"
                  onClick={handleNext}
                  disabled={!isVerified.email || !isVerified.mobile}
                  style={{ padding: 24 }}
                >
                  Next
                </CustomButton>
              )}

              {currentStep === 2 && (
                <CustomButton
                  loading={loading}
                  type="primary"
                  htmlType="submit"
                  style={{ padding: 24 }}
                >
                  Submit
                </CustomButton>
              )}
            </Flex>
          )}

          {error && (
            <Alert
              message={error}
              type="error"
              showIcon
              style={{ marginTop: 20 }}
            />
          )}
        </div>
      </Form>

      {otpModalOpen && (
        <CustomModal
          open={otpModalOpen}
          centered
          onCancel={() => setOtpModalOpen(false)}
          content={
            <OtpVerification
              contactType={otpContext.type}
              contactValue={otpContext.value}
              setIsVerified={setIsVerified}
              setOtpModalOpen={setOtpModalOpen}
              onVerifySuccess={() => {
                if (otpContext.type === "email") {
                  setVerifiedEmail(email);
                } else {
                  setVerifiedMobile(phoneNumber);
                }
              }}
            />
          }
        />
      )}
    </div>
  );

  const successGif = (
    <div className="auth-signup-container success-container">
      <img
        src={SuccessGif}
        alt="success"
        className="success-gif"
      />

      <h2>Account Created Successfully</h2>

      <p>
        Your login details have been sent to your registered email. Please log in to continue.
      </p>

      <CustomButton
        type="primary"
        className="login-btn"
        block
        onClick={() => navigate("/login")}
      >
        Login
      </CustomButton>
    </div>
  );

  return <AuthDesign authForm={showSuccessGif ? successGif : signupForm} formType="signup" />;
};
export default Signup;
